import ProtectedService from "services/protected.service"
import { objectToQueryString } from "utils/funcs"
import {
  ECARDS_VIVINT_BASE_URL,
  MOBILE_API_URL,
  SNOW_FLAKE_URL,
} from "utils/apiConfig"

import axios from "axios"

class UserService extends ProtectedService {
  /** Ecards API root for Vivint features; absolute URL so requests bypass dashboard-apis `baseURL` when set. */
  private vivintEcardsAbsolute(path: string): string {
    if (!ECARDS_VIVINT_BASE_URL) return path
    const base = ECARDS_VIVINT_BASE_URL.replace(/\/$/, "")
    let p = path.startsWith("/") ? path : `/${path}`
    // Base …/api/v1 + path /api/vivint/… → …/api/v1/vivint/… (not …/api/v1/api/vivint/…)
    if (/\/api\/v1$/i.test(base) && p.startsWith("/api/")) {
      p = p.replace(/^\/api\//, "/")
    }
    return `${base}${p}`
  }

  /** Weekly-training paths when opened under Vivint Ads → Weekly Trainings. */
  private withEcardsWeekly(path: string, useEcardsVivint?: boolean): string {
    if (!useEcardsVivint || !ECARDS_VIVINT_BASE_URL) return path
    const p = path.startsWith("/") ? path : `/${path}`
    return `${ECARDS_VIVINT_BASE_URL.replace(/\/$/, "")}${p}`
  }

  private applyAxiosAuthHeader() {
    const token = this.getAuthHeader()
    if (token) {
      axios.defaults.headers.common["x-auth-token"] = token
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common["x-auth-token"]
      delete axios.defaults.headers.common.Authorization
    }
  }

  async getMyTrips(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/evo/dashboard/my-trips/all-trips?${queryString}`)
  }

  async getGroupByMonth(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/evo/dashboard/my-trips/group-by-month?${queryString}`)
  }

  async getGroupByMonthOperations(pagination: any, receiptPaidFilter?: string) {
    const q: Record<string, any> = { ...pagination }
    if (receiptPaidFilter && receiptPaidFilter !== "all") {
      q.receiptPaidFilter = receiptPaidFilter
    }
    const queryString = objectToQueryString(q)
    return await this.get(`/evo/dashboard/my-trips/group-by-month-operations?${queryString}`)
  }

  async getReceiptTransactions(
    pagination: any,
    category: "all" | "trips" | "operations" = "all",
    options?: {
      /** false = Operations tab includes open/unsubmitted operation docs */
      submittedOnly?: boolean
      /** asc = oldest added first (default for receipt list) */
      sortOrder?: "asc" | "desc"
    }
  ) {
    const q: Record<string, any> = { ...pagination, category }
    if (options?.submittedOnly === false) {
      q.submittedOnly = "false"
    }
    if (options?.sortOrder === "desc") {
      q.sortOrder = "desc"
    } else if (options?.sortOrder === "asc") {
      q.sortOrder = "asc"
    }
    const queryString = objectToQueryString(q)
    return await this.get(`/evo/dashboard/receipt-transactions?${queryString}`)
  }

  async updateReceiptPaidStatus(data: {
    source: "myTrips" | "operations"
    parentId: string
    expenseId: string
    receiptPaidStatus: "Paid" | "Not-Paid"
  }) {
    return await this.post(`/evo/dashboard/receipt-transactions/paid-status`, data)
  }

  async getMyTripDetail(id: string) {
    return await this.get(`/evo/my-trips/detail/${id}`)
  }

  async deleteMyTrip(id: string) {
    return await this.delete(`/evo/my-trips/delete-trip/${id}`)
  }

  async addTrip(data: { badgeId: string; startDate: string; endDate: string; cityName: string; state: string; description?: string }) {
    return await this.post(`/evo/my-trips/add-trip`, data)
  }

  async updateMyTrip(id: string, data: any) {
    return await this.post(`/evo/my-trips/update/${id}`, data)
  }

  async updateOperation(id: string, data: any) {
    return await this.post(`/evo/operations/update/${id}`, data)
  }

  async updateMyTripExpense(data: any) {
    return await this.post(`/evo/my-trips/update/expenses`, data)
  }

  async deleteMyTripExpense(data: { tripId: string; expenseId: string }) {
    return await this.post(`/evo/my-trips/delete-expense`, data)
  }

  async getReimbursements({ limit, page }) {
    this.applyAxiosAuthHeader()
    return await this.get(
      `${SNOW_FLAKE_URL}/helper/reimbursements?limit=${limit}&skip=${page - 1}`
    )
  }
  async getRepAndReimbursements() {
    return await this.get(`/rep_and_regional_reimbursement/receipt`)
  }

  async updateRSVP(
    data,
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.post(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/move_rsvp_to_attendees`,
        opts?.useEcardsVivint
      ),
      data
    )
  }

  async updateRSVPStatus(
    data,
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.post(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/update_rsvp_status`,
        opts?.useEcardsVivint
      ),
      data
    )
  }

  async updateAttendees(
    data,
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.post(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/update_training_attendees_status`,
        opts?.useEcardsVivint
      ),
      data
    )
  }

  async getRepReimbursements({ startDate, endDate }) {
    return await this.get(
      `/rep_and_regional_reimbursement/rep_receipt?startDate=${startDate}&endDate=${endDate}`
    )
  }

  async getWeeklyStats({ type, date }) {
    return await this.get(
      `/dashboard_weekly_training/stats?date=${date}&meetingType=${type}`
    )
  }
  async getBlitzStats(date, maxPosition) {
    return await this.get(`/blitz_dash_board/blitz_stats?date=${date}&maxPosition=${maxPosition}`)
  }

  /** Blitz users regional report: GET with query date, groupBy, isRegistered, position, maxPosition, tenure, salesYear */
  async getBlitzUsersRegionalReporting(params: Record<string, string | undefined>) {
    const q: Record<string, string> = {}
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/blitz_dash_board/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  /** Blitz: filter dropdown options (positions, maxPositions, tenures, salesYears). GET */
  async getBlitzFilterOptions() {
    return await this.get("/blitz_dash_board/filter_options")
  }

  /** Weekly Trainings regional report: GET with query date, dateFrom, dateTo, groupBy, type, connectType, connectFormat, state */
  async getTrainingsRegionalReporting(params: Record<string, string | undefined>) {
    const q: Record<string, string> = {}
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/trainings_dash_board/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  async getTrainingsFilterOptions() {
    return await this.get("/trainings_dash_board/filter_options")
  }

  async getBreezyState(date) {
    return await this.get(`/breezy_dash_board/breezy_stats?date=${date}`)
  }
  
  async getRegionalReimbursements({ startDate, endDate }) {
    return await this.get(
      `/rep_and_regional_reimbursement/regional_receipt?startDate=${startDate}&endDate=${endDate}`
    )
  }
  async locationRaffles(
    ids,
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.get(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/weekly_location_raffle_entries?training_id=${ids}`,
        opts?.useEcardsVivint
      )
    )
  }

  async getEcards({ pageSize, name, pageNumber, endDate, startDate }) {
    return await this.get(
      `/e_card_dash_board/all_e_card?pageNumber=${pageNumber}&pageSize=${pageSize}&startDate=${startDate}&endDate=${endDate}&name=${name}`
    )
  }

  async deleteEcard(id) {
    return await this.delete(`/e_card_dash_board/delete_e_card/${id}`)
  }

  async getBreezyData({ limit, pageNumber, endDate, startDate }) {
    return await this.get(
      `/breezy_dash_board/all_candidates?page=${pageNumber}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`
    )
  }
  
  async getRaffleWinners(opts?: { useEcardsVivint?: boolean }) {
    if (opts?.useEcardsVivint) {
      return await this.get(
        this.vivintEcardsAbsolute(
          `/api/vivint/connect/dashboard/all-raffle-winners`
        )
      )
    }
    return await this.get(`/dashboard_weekly_training/all-raffle-winners`)
  }

  async getTrainingReports(opts?: { useEcardsVivint?: boolean }) {
    return await this.get(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/week-wise-training-reports`,
        opts?.useEcardsVivint
      )
    )
  }

  async changeRepStatus(id: string, status: string) {
    return await this.post(
      `/rep_and_regional_reimbursement/update_rep_receipet/${id}`,
      { status }
    )
  }

  /** Maps UI query keys to candidates_dash_board API (e.g. outreach → outReach). */
  private normalizeCandidatesDashboardQuery(state: Record<string, any> | null | undefined) {
    const q = { ...(state || {}) }
    if (Object.prototype.hasOwnProperty.call(q, "outreach")) {
      q.outReach = q.outreach
      delete q.outreach
    }
    return q
  }

  /**
   * Signed reps: dashboard-apis (`elastic-vivint` get_signed_reps) reads **camelCase**
   * query keys (`repId`, `repName`, `managementGroup`, `maxPosition`). Some ecards routes
   * expect snake_case — we send both when a value is set.
   * Drops blank string params so we do not send `email=` etc.
   * Sends `exactMatch=true` when searching by rep id, rep name, or email so the API can
   * match whole values instead of substring regex on those fields.
   */
  private normalizeSignedRepsQueryParams(
    state: Record<string, unknown> | null | undefined
  ): Record<string, unknown> {
    const p: Record<string, unknown> = { ...(state || {}) }

    const out: Record<string, unknown> = {}
    for (const key of Object.keys(p)) {
      const v = p[key]
      if (v === undefined || v === null) continue
      if (typeof v === "string") {
        const t = v.trim()
        if (t === "") continue
        out[key] = t
      } else {
        out[key] = v
      }
    }

    const mirror = (camel: string, snake: string) => {
      const v = out[camel]
      if (v != null && String(v).trim() !== "") {
        out[snake] = String(v).trim()
      }
    }
    mirror("repId", "rep_id")
    mirror("repName", "rep_name")
    mirror("managementGroup", "management_group")
    mirror("maxPosition", "max_position")

    const repIdQ = out.repId ?? out.rep_id
    const repNameQ = out.repName ?? out.rep_name
    const emailQ = out.email
    if (
      (repIdQ != null && String(repIdQ).trim() !== "") ||
      (repNameQ != null && String(repNameQ).trim() !== "") ||
      (emailQ != null && String(emailQ).trim() !== "")
    ) {
      out.exactMatch = "true"
    }

    return out
  }

  async getCanidates(state: any) {
    const queryString = objectToQueryString(
      this.normalizeCandidatesDashboardQuery(state)
    )
    return await this.get(`/candidates_dash_board/getCandidates?${queryString}`)
  }

  async downloadCandidates(state: any) {
    const queryString = objectToQueryString(
      this.normalizeCandidatesDashboardQuery(state)
    )
    return await this.get(`/candidates_dash_board/getCandidates?${queryString}`)
  }

  /** Vivint Ads dashboard — list ads (`/api/vivint-ads/dashboard/all-ads`). Optional filters as query params. */
  async getVivintAdsDashboardAllAds(
    params?: Record<string, string | undefined>
  ) {
    const queryString = objectToQueryString(params || {})
    const path = queryString
      ? `/api/vivint-ads/dashboard/all-ads?${queryString}`
      : `/api/vivint-ads/dashboard/all-ads`
    return await this.get(this.vivintEcardsAbsolute(path))
  }

  /** Same route with `isDownload` + filters; response may be raw CSV text. */
  async downloadVivintAdsDashboardCsv(
    params?: Record<string, string | undefined>
  ) {
    const merged = { ...(params || {}), isDownload: "true" }
    const queryString = objectToQueryString(merged)
    const path = `/api/vivint-ads/dashboard/all-ads?${queryString}`
    return await this.get(this.vivintEcardsAbsolute(path), {
      responseType: "text",
    })
  }

  /** Single ad for Vivint Ads dashboard detail view. */
  async getVivintAdDashboardDetail(id: string) {
    return await this.get(
      this.vivintEcardsAbsolute(`/api/vivint-ads/dashboard/ad-detail/${id}`)
    )
  }

  /** Update ad from Vivint Ads dashboard (same payload shape as recruiting media ad update). */
  async updateVivintDashboardAd(id: string, data: unknown) {
    return await this.post(
      this.vivintEcardsAbsolute(`/api/vivint-ads/dashboard/update-ad/${id}`),
      data
    )
  }

  /** Vivint Ads candidates: `/api/vivint/dashboard/candidates/all-candidates` (under ecards `/api/v1`). */
  async getVivintCanidates(state: any) {
    const queryString = objectToQueryString(
      this.normalizeCandidatesDashboardQuery(state)
    )
    return await this.get(
      this.vivintEcardsAbsolute(
        `/api/vivint/dashboard/candidates/all-candidates?${queryString}`
      )
    )
  }

  /** Same route as list with export query; backend may return raw CSV instead of JSON. */
  async downloadVivintCandidates(state: any) {
    const queryString = objectToQueryString(
      this.normalizeCandidatesDashboardQuery(state)
    )
    return await this.get(
      this.vivintEcardsAbsolute(
        `/api/vivint/dashboard/candidates/all-candidates?${queryString}`
      ),
      { responseType: "text" }
    )
  }

  async boardStates() {
    return await this.get(`${SNOW_FLAKE_URL}/board/get_states`)
  }

  async getCompanies() {
    return await this.get(`${SNOW_FLAKE_URL}/dealership/getcompanies`)
  }

  async companyDetails(id: string) {
    return await this.get(
      `${SNOW_FLAKE_URL}/dealership/getcompany_details/${id}`
    )
  }

  async myOffices(id: string) {
    return await this.get(`/dealer_office/my-office?companyId=${id}`)
  }

  async companyUsers(id: string, badgeId: string) {
    return await this.get(
      `${SNOW_FLAKE_URL}/dealership/list_users/${id}/${badgeId}`
    )
  }

  async getCanidateStats(state: any) {
    const queryString = objectToQueryString(state)
    return await this.get(
      `${SNOW_FLAKE_URL}/board/candidate_dashboard?${queryString}`
    )
  }

  /** @deprecated typo — use downloadCandidates; kept for callers that still use this name */
  async downloadCanidates(state: any) {
    return await this.downloadCandidates(state)
  }

  async deleteContact(id) {
    return await this.delete(`${SNOW_FLAKE_URL}/contact/delete/${id}`)
  }
  async getBlackSparrow({ date, flag, type }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/candidate_states?date=${date}&flag=${flag}&type=${type}`
    )
  }

  async getBlackSparrowStats({ type, date, flag }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/black_sparrow_states?type=${type}&date=${date}&flag=${flag}`
    )
  }
  async getBlackSparrowStatsLocations({ date, flag }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/candidate_location_states?date=${date}&flag=${flag}`
    )
  }

  async getSparrowReport(id) {
    return await this.get(`${SNOW_FLAKE_URL}/board/candidate_states/${id}`)
  }

  async getSparrowReportLocation(location) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/candidate_location_details?location=${location}`
    )
  }

  async deleteReimbursement(id) {
    return await this.delete(
      `/rep_and_regional_reimbursement/delete_receipt/${id}`
    )
  }

  async getLicenses(state: any) {
    return await this.post(`/licensing/all_licenses`, state)
  }

  async getSummary(state) {
    this.applyAxiosAuthHeader()
    return await this.post(`${SNOW_FLAKE_URL}/contact/summary`, state)
  }

  async getDealership() {
    this.applyAxiosAuthHeader()
    return await this.get(`${SNOW_FLAKE_URL}/auth/list_dealers`)
  }

  async addCompany(payload: any) {
    this.applyAxiosAuthHeader()
    return await this.post(
      `${SNOW_FLAKE_URL}/dealership/create_company`,
      payload
    )
  }

  async getTNTWinners(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/evo/tnt/winner?${queryString}`)
  }

  async getOperations(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/evo/dashboard/operations/all?${queryString}`)
  }

  async createOperation(data: {
    badgeId: string
    monthName: string
    [key: string]: any
  }) {
    return await this.post(`/evo/operations/create`, data)
  }

  async deleteOperationExpense(expenseId: string, operationId: string) {
    return await this.post(`/evo/operations/delete-operation-expense`, {
      expenseId,
      operationId
    })
  }

  async addExpenseToOperation(operationId: string, data: FormData) {
    return await this.postWithParams(
      `/evo/operations/add-expense-ai/${operationId}`,
      data
    )
  }

  async addExpenseToTrip(tripId: string, data: FormData) {
    return await this.postWithParams(
      `/evo/my-trips/add-expense-ai/${tripId}`,
      data
    )
  }

  async updateOperationExpense(data: any) {
    return await this.post(`/evo/operations/update/expenses`, data)
  }

  async operationDetails(operationId: string) {
    return await this.get(`/evo/operations/detail/${operationId}`)
  }

  async deleteOperation(operationId: string) {
    return await this.delete(`/evo/operations/delete/${operationId}`)
  }

  async deleteTNTWinner(id: string) {
    return await this.delete(`/evo/tnt/winner/delete/${id}`)
  }

  async addTNTWinner(data: any) {
    return await this.post(`/evo/tnt/winner/add`, data)
  }

  async updateTNTWinner(id: string, data: any) {
    return await this.post(`/evo/tnt/winner/update/${id}`, data)
  }

  async deleteDealership(id: any) {
    this.applyAxiosAuthHeader()
    return await this.get(`${SNOW_FLAKE_URL}/auth/delete_user/${id}`)
  }

  async deleteCompany(id: any) {
    this.applyAxiosAuthHeader()
    return await this.delete(
      `${SNOW_FLAKE_URL}/dealership/delete_company/${id}`
    )
  }

  async deleteCompanyUser(id: any) {
    this.applyAxiosAuthHeader()
    return await this.delete(`${SNOW_FLAKE_URL}/dealership/delete_user/${id}`)
  }

  async deleteCompanyLocation(companyId: any, id: any) {
    this.applyAxiosAuthHeader()
    return await this.delete(
      `${SNOW_FLAKE_URL}/dealership/company/${companyId}/offices/${id}`
    )
  }

  async deleteOffice(id: any) {
    return await this.delete(`/dealer_office/delete/${id}`)
  }

  async addCompanyLocation(id: any, state: any) {
    return await this.post(`/dealer_office/add`, { badgeId: id, ...state })
  }

  async getDealerOffices(id: any) {
    this.applyAxiosAuthHeader()
    return await this.get(`${SNOW_FLAKE_URL}/dealership/company/${id}/offices`)
  }

  async deleteCalendar(payload: any) {
    return await this.delete(
      `/social_media_ads/delete_adminify_calendar/${payload}`
    )
  }

  async addLocation(state: any) {
    return await this.post(`/recruitment_center_location/add_location`, state)
  }

  async addDealerCandidate(state: any) {
    return await this.post(`/dealer_candidate/add_candidate`, state)
  }

  async addEvoCandidate(state: any) {
    return await this.post(`/candidates_dash_board/add_candidate`, state)
  }

  /** Vivint Ads candidates — POST `/vivint/candidates/add-candidate` with Evo-style body (first/last/email/phone/ad/source/candidate). */
  async addVivintCandidate(state: any) {
    const body = {
      first_name: state.first_name,
      last_name: state.last_name,
      email: state.email,
      phone: state.phone,
      social_media_ad_id: state.social_media_ad_id,
      source: state.source,
      candidate: state.candidate,
    }
    return await this.post(
      this.vivintEcardsAbsolute(`/vivint/candidates/add-candidate`),
      body
    )
  }

  async updateLocation(state: any) {
    return await this.post(
      `/recruitment_center_location/update_location`,
      state
    )
  }
  async updateCalendar(state: any) {
    return await this.post(
      `/social_media_ads/update_adminify_calendar_meet_link`,
      state
    )
  }
  async addTraining(
    state: any,
    opts?: { useEcardsVivint?: boolean }
  ) {
    const path = `/trainings/add`
    return await this.post(
      opts?.useEcardsVivint
        ? this.withEcardsWeekly(path, true)
        : `${SNOW_FLAKE_URL}${path}`,
      state
    )
  }

  async updateStatus(id: string) {
    return await this.get("/helper/changestatus/" + id)
  }

  async getCategories() {
    return await this.get(`${SNOW_FLAKE_URL}/videos/all`)
  }

  async getUsers() {
    return await this.get(`${SNOW_FLAKE_URL}/dashboard/list_users`)
  }

  async getOffices() {
    return await this.get(
      `${SNOW_FLAKE_URL}/partner/offices?partner=Jason Card`
    )
  }

  async deleteCategory(id: string) {
    return await this.delete(`${SNOW_FLAKE_URL}/videos/delete/` + id)
  }
  async deleteFeedback(id: string) {
    return await this.delete(`/feedbacks/delete/` + id)
  }

  async updateFeedback(id: string, status: string) {
    return await this.post(`/feedbacks/update_feedback`, {
      id,
      status,
    })
  }

  async getCatVideos(category: string) {
    return await this.get(`${SNOW_FLAKE_URL}/videos/allvideos/` + category)
  }

  async addCategory(data: { title: string; category_image: File }) {
    const formData = new FormData()
    formData.append("title", data.title)
    formData.append("category_image", data.category_image)
    return await this.post("/videos/add", formData)
  }

  async getOpportunities(pagination: any) {
    const queryString = objectToQueryString(pagination)

    return await this.get(`/get_opportunitiess?${queryString}`)
  }

  async downloadOpportunities(pagination: any) {
    const queryString = objectToQueryString(pagination)

    return await this.get(`/download_opportunities?${queryString}`)
  }

  async getAllSolarLeads(pagination: any) {
    const p = { ...pagination }
    if (p.pageNumber != null && p.currentPage == null) {
      p.currentPage = p.pageNumber
      delete p.pageNumber
    }
    const queryString = objectToQueryString(p)

    return await this.get(
      `/solar_leads_management/dash_board/get_solar_leads?${queryString}`
    )
  }

  async getOnboarding(pagination: any) {
    const queryString = objectToQueryString(pagination)

    return await this.get(`/dealer/onboarding?${queryString}`)
  }

  async deleteSolarLead(id: any) {
    return await this.delete(`/solar_leads_management/delete_solar_lead/${id}`)
  }

  async deleteOnboarding(id: any) {
    return await this.delete(`/onboarding/delete/${id}`)
  }

  async getLeads(pagination: any) {
    const p = { ...pagination }
    if (p.pageNumber != null && p.currentPage == null) {
      p.currentPage = p.pageNumber
      delete p.pageNumber
    }
    const queryString = objectToQueryString(p)
    return await this.get(`/get_solar_leads?${queryString}`)
  }

  async adminifyCalendar() {
    return await this.get(`/social_media_ads/list_adminify_calendar`)
  }

  async getContactStates(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(
      `/contacts_states_with_respect_to_management?${queryString}`
    )
  }

  async getContactLeader(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(
      `/contacts_states_with_respect_to_contact_leader?${queryString}`
    )
  }

  async downloadLeads(pagination: any) {
    const p = { ...pagination }
    if (p.pageNumber != null && p.currentPage == null) {
      p.currentPage = p.pageNumber
      delete p.pageNumber
    }
    const queryString = objectToQueryString(p)
    return await this.get(`/get_solar_leads_download?${queryString}`)
  }
  async getVivintLeads(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`${SNOW_FLAKE_URL}/board/vivint_leads?${queryString}`)
  }

  async getSignedReps(pagination: any) {
    const queryString = objectToQueryString(
      this.normalizeSignedRepsQueryParams(pagination)
    )

    return await this.get(
      `/signed_reps_dash_board/get_signed_reps?${queryString}`
    )
  }

  async getEmployeeRoster(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(
      `/employee_roster_dash_board/get_employee_roster?${queryString}`
    )
  }

  async getEmployeeRosterFieldValues(params: { field: string; q?: string }) {
    const queryString = objectToQueryString({
      field: params.field,
      q: params.q ?? "",
    })
    return await this.get(
      `/employee_roster_dash_board/get_employee_roster_field_values?${queryString}`
    )
  }

  /** Fetch unique divisions for signed reps filters (dropdown options). Expects { success, data: string[] }. */
  async getSignedRepsDivisions(year: string) {
    const q = year ? `?year=${encodeURIComponent(year)}` : ""
    return await this.get(
      `/signed_reps_dash_board/get_signed_reps_divisions${q}`
    )
  }

  /** Fetch leaders by role for signed reps filters (dropdown options). Expects { success, data: { id: string, name: string }[] }. */
  async getSignedRepsLeaders(year: string, maxPosition: string) {
    const params = new URLSearchParams()
    if (year) params.set("year", year)
    if (maxPosition) params.set("maxPosition", maxPosition)
    const q = params.toString() ? `?${params.toString()}` : ""
    return await this.get(
      `/signed_reps_dash_board/get_signed_reps_leaders${q}`
    )
  }

  async getSolarLead() {
    return await this.get(
      `/solar_leads_management/fetch_acuity_calendar_office`
    )
  }
  async solarDetails(id: string) {
    return await this.get(
      `/solar_leads_management/calendar_office_detail/${id}`
    )
  }

  async getList(opts?: { useEcardsVivint?: boolean }) {
    const path = `/helper/downline/2719`
    return await this.get(
      opts?.useEcardsVivint
        ? this.withEcardsWeekly(path, true)
        : `${SNOW_FLAKE_URL}${path}`
    )
  }

  async getSolarStats(id: string) {
    return await this.get(
      `/solar_leads_management/get_solar_closure_stats?officeId=${id}`
    )
  }

  async downloadSignedReps(pagination: any) {
    const p = { ...pagination }
    delete p.pageNumber
    delete p.pageSize
    const queryString = objectToQueryString(
      this.normalizeSignedRepsQueryParams(p)
    )

    return await this.get(
      `/signed_reps_dash_board/download_signed_reps?${queryString}`
    )
  }

  async getTechOffices({ limit, page, pageNumber, pageSize }: {
    limit?: number
    page?: number
    pageNumber?: number
    pageSize?: number
  }) {
    this.applyAxiosAuthHeader()
    if (!SNOW_FLAKE_URL) {
      throw new Error("REACT_APP_SNOWFLAKE_API_URL is not configured.")
    }
    const resolvedPage = pageNumber ?? page ?? 1
    const resolvedLimit = pageSize ?? limit ?? 50
    return await axios.get(
      `${SNOW_FLAKE_URL}/board/tech_offices?limit=${resolvedLimit}&skip=${
        resolvedPage - 1
      }`
    )
  }

  async updateCompanyDetails(id: any, data: any) {
    this.applyAxiosAuthHeader()
    return await this.post(
      `${SNOW_FLAKE_URL}/dealership/update_company/${id}`,
      data
    )
  }

  async getProduction(filters: any) {
    this.applyAxiosAuthHeader()
    if (!MOBILE_API_URL) {
      throw new Error("REACT_APP_MOBILE_API_URL is not configured.")
    }
    return await axios.post(`${MOBILE_API_URL}/filter_efp_up_pmr`, filters)
  }

  async getMediaAds(pagination: any) {
    const queryString = objectToQueryString(pagination)

    return await this.get(`/social_media_ads/all_ads?${queryString}`)
  }
  async deleteMedia(id: any) {
    return await this.delete(`/media_ads/delete_add/${id}`)
  }
  async updateContactAd(data: any, id: any) {
    return await this.post(`/dealer_contact/update_contact/${id}`, data)
  }

  async deleteContactAd(id: any) {
    return await this.post(`/dealer_contact/delete_contact`, { id })
  }

  async getMediaAdDetail(id: any) {
    return await this.get(`/social_media_ads/ad_detail_dash_board_api/${id}`)
  }

  async getDealerAdDetail(id: any) {
    return await this.get(`/dealers_social_media_add/ad-detail/${id}`)
  }

  async getCollaborators(id: any) {
    return await this.get(`/dealers_social_media_add/view_collaborator/${id}`)
  }

  async addCollaborator(data: any) {
    return await this.post(`/dealers_social_media_add/addCollaborator`, data)
  }

  async deleteBlitzUser (blitzId: any, userId: any) {
    return await this.delete(`${SNOW_FLAKE_URL}/blitz/deleteuser/${blitzId}/${userId}`)
  }

  async removeCollaborator(data: any) {
    return await this.post(
      `/dealers_social_media_add/remove_collaborator`,
      data
    )
  }

  async dealerTrainingsUsers(id: any) {
    return await this.get(`/dealers_training/company-users/${id}`)
  }

  async getMediaStats(params) {
    const queryString = objectToQueryString(params)
    return await this.get(`/social_media_ads/ad_stats?${queryString}`)
  }

  async dealerCandidates(params) {
    const queryString = objectToQueryString(params)
    return await this.get(
      `/dealer_candidate/dash_board_candidates?${queryString}`
    )
  }

  async addMediaDetail(id: any) {
    return await this.get(`/social_media_ads/ad_detail/${id}`)
  }

  async addMediaAd(payload: any) {
    return await this.post(`/social_media_ads/add`, payload)
  }

  async updateMediaAd(id: any, data: any) {
    return await this.post(`/social_media_ads/update_ad/${id}`, data)
  }

  /** Regional Report: groupBy, leaderId (string or string[]), date */
  async getRegionalReporting(params: {
    groupBy?: string
    leaderId?: string | string[]
    date?: string
  }) {
    const q: Record<string, string> = {}
    if (params.groupBy) q.groupBy = params.groupBy
    if (params.date) q.date = params.date
    if (params.leaderId != null) {
      q.leaderId = Array.isArray(params.leaderId)
        ? params.leaderId.filter(Boolean).join(",")
        : String(params.leaderId)
    }
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/social_media_ads/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  /** Mobile Report: filter by date, tag (position), tenure, users. POST body: { date, tag, tenure?, users? } */
  async mobileFilter(params: { date: string; tag: string; tenure?: string; users?: string }) {
    return await this.post("/mobile/filter", {
      date: params.date,
      tag: params.tag,
      tenure: params.tenure ?? "All",
      users: params.users ?? "All",
    })
  }

  /** Ranks Report: filter by date, filter (metric), tag, group_by, tenure, users. POST body */
  async ranksFilter(params: {
    date: string
    filter: string
    tag: string
    group_by: string
    tenure?: string
    users?: string
    userid?: string
  }) {
    return await this.post("/ranks/filter", {
      date: params.date,
      filter: params.filter,
      tag: params.tag,
      group_by: params.group_by,
      tenure: params.tenure ?? "All",
      users: params.users ?? "All",
      userid: params.userid,
    })
  }

  /** Ranks Report: get users by position for dropdown. POST body: { tag } */
  async ranksUsersByPosition(tag: string) {
    return await this.post("/ranks/users", { tag })
  }

  /** Ranks Report: get details for a row. POST body: date, filter, tag, group_by, name, users?, userid?, tenure? */
  async ranksGetDetails(params: {
    date: string
    filter: string
    tag: string
    group_by: string
    name: string
    users?: string
    userid?: string
    tenure?: string
  }) {
    return await this.post("/ranks/get_details", params)
  }

  /** Mobile Report: get users list by position for dropdown. POST body: { tag }, optional query badgeid */
  async mobileUsersByPosition(tag: string, badgeid?: string | null) {
    const q = badgeid != null ? `?badgeid=${encodeURIComponent(badgeid)}` : ""
    return await this.post(`/mobile/users${q}`, { tag })
  }

  /** Candidates Regional Report: GET with query date, groupBy, leaderId, candidateStage, evoStage, source, sourceCategory, recruitingLocation, outReach, screeningResult, salesLeaderId, interviewResult, agreementSigned, tenure (e.g. Rookie,Sophomore) */
  async getCandidatesRegionalReporting(params: Record<string, string | undefined>) {
    const q: Record<string, string> = {}
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/candidates_dash_board/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  /** Candidates: get sales leaders list for dropdown. GET */
  async getCandidatesSalesLeaders() {
    return await this.get("/candidates_dash_board/sales_leaders")
  }

  /** Candidates: get filter dropdown options (candidateStages, evoStages, sources, sourceCategories, recruitingLocations). GET */
  async getCandidatesFilterOptions() {
    return await this.get("/candidates_dash_board/filter_options")
  }

  /** Mobile Report: dropdown drill-down. POST body: { date, tag, tenure?, users?, group } */
  async mobileDropdown(params: {
    date: string
    tag: string
    tenure?: string
    users?: string
    group: string
  }) {
    return await this.post("/mobile/dropdown", {
      date: params.date,
      tag: params.tag,
      tenure: params.tenure ?? "All",
      users: params.users ?? "All",
      group: params.group,
    })
  }

  /** Signed Reps Regional Report: groupBy, leaderId, date, year, recruiterId, recruiterName, maxPosition, tenure, onboardedDate, division */
  async getSignedRepsRegionalReporting(params: {
    groupBy?: string
    leaderId?: string | string[]
    date?: string
    year?: string
    recruiterId?: string
    recruiterName?: string
    maxPosition?: string
    tenure?: string
    onboardedDate?: string
    division?: string | string[]
  }) {
    const q: Record<string, string> = {}
    if (params.groupBy) q.groupBy = params.groupBy
    if (params.date) q.date = params.date
    if (params.year) q.year = params.year
    if (params.onboardedDate) q.onboardedDate = params.onboardedDate
    if (params.recruiterId) q.recruiterId = params.recruiterId
    if (params.recruiterName) q.recruiterName = params.recruiterName
    if (params.maxPosition) q.maxPosition = params.maxPosition
    if (params.tenure) q.tenure = params.tenure
    if (params.division != null) {
      q.division = Array.isArray(params.division) ? params.division.filter(Boolean).join(",") : String(params.division)
    }
    if (params.leaderId != null) {
      q.leaderId = Array.isArray(params.leaderId)
        ? params.leaderId.filter(Boolean).join(",")
        : String(params.leaderId)
    }
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/signed_reps_dash_board/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  async addSolarLead(data: any) {
    return await this.post(`/solar_leads_management/add_solar_lead`, data)
  }

  async getSaleOffices({ limit, page, pageNumber, pageSize }: {
    limit?: number
    page?: number
    pageNumber?: number
    pageSize?: number
  }) {
    this.applyAxiosAuthHeader()
    if (!SNOW_FLAKE_URL) {
      throw new Error("REACT_APP_SNOWFLAKE_API_URL is not configured.")
    }
    const resolvedPage = pageNumber ?? page ?? 1
    const resolvedLimit = pageSize ?? limit ?? 50
    return await axios.get(
      `${SNOW_FLAKE_URL}/board/sale_offices?limit=${resolvedLimit}&skip=${
        resolvedPage - 1
      }`
    )
  }

  async getCampusReels({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/campus_reel?limit=${limit}&skip=${page - 1}`
    )
  }

  async getRecruiterLocations({ year }: { year: string }) {
    return await this.get(`/recruitment_center_location/locations?year=${year}`)
  }

  async deleteLocation(id: string) {
    return await this.delete(
      `/recruitment_center_location/delete_location/${id}`
    )
  }

  async getBreezy({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/breezy?limit=${limit}&skip=${page - 1}`
    )
  }

  async getPositions({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/positions?limit=${limit}&skip=${page - 1}`
    )
  }

  async getDigitalHire({ limit, page }) {
    return await this.get(`/board/digital_hire?limit=${limit}&skip=${page - 1}`)
  }

  async getDigitalHireSocial({ limit, page }) {
    return await this.get(
      `/board/digital_hire_social?limit=${limit}&skip=${page - 1}`
    )
  }

  async deleteVideo(id: string) {
    return await this.delete(`${SNOW_FLAKE_URL}/videos/deletevideo/` + id)
  }

  async getCandidateFilters() {
    return await this.get(`${SNOW_FLAKE_URL}/board/get_candidate_filters`)
  }

  async getDealerReports() {
    return await this.get(`/dealer_candidate/companies_stats`)
  }

  async getDealerCompanyReports(companyId) {
    return await this.get(`/dealer_candidate/companies_stats/${companyId}`)
  }

  async deleteCandidate(id) {
    return await this.delete(`/candidates_dash_board/delete_candidate/${id}`)
  }

  async deleteVivintCandidate(id: string) {
    return await this.delete(
      this.vivintEcardsAbsolute(
        `/api/vivint/dashboard/candidates/delete-candidate/${id}`
      )
    )
  }

  async deleteAdCandidate(id) {
    return await this.delete(`/dealer_candidate/delete_candidate/${id}`)
  }

  async getWeeklyTrainings(
    pagination,
    opts?: { useEcardsVivint?: boolean }
  ) {
    const queryString = objectToQueryString(pagination)

    if (opts?.useEcardsVivint) {
      return await this.get(
        this.vivintEcardsAbsolute(
          `/api/vivint/connect/dashboard/all-training?${queryString}`
        )
      )
    }

    return await this.get(
      `/dashboard_weekly_training/all_training?${queryString}`
    )
  }

  async deleteTraining(id, opts?: { useEcardsVivint?: boolean }) {
    return await this.delete(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/delete/${id}`,
        opts?.useEcardsVivint
      )
    )
  }

  async deleteDealerTraining(id) {
    return await this.delete(`/dealers_training/delete_training/${id}`)
  }

  async getBlitz(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/blitz_dash_board/all_blitz?${queryString}`)
  }

  // async getBlitzUsers({ limit, page, key }) {
  //   return await this.get(
  //     `${SNOW_FLAKE_URL}/board/blitz_users?limit=${limit}&skip=${
  //       page - 1
  //     }&blitz_key=${key}`
  //   );
  // }

  async getBlitzDetails(id) {
    return await this.get(`${SNOW_FLAKE_URL}/blitz/${id}`)
  }

  async assignSignedRepToBlitz(payload) {
    return await this.post(`${SNOW_FLAKE_URL}/blitz/assign_signedrep_to_blitz`, payload)
  }
  async getRegisteredBlitz(params: Record<string, unknown>) {
    const p = { ...params }
    return await this.get(
      `/blitz_dash_board/registered_blitz_user?${objectToQueryString(p)}`
    )
  }

  async getSparrowUsers(params) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/black_sparrow_states_users?date=${params.date}&flag=${params.flag}`
    )
  }

  async getAquity({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/aquity?limit=${limit}&skip=${page - 1}`
    )
  }

  // async getRaffles(id) {
  //   return await this.get(`/trainings/weekly_training_details/${id}`);
  // }

  async getSpecificTraining(id, opts?: { useEcardsVivint?: boolean }) {
    if (opts?.useEcardsVivint) {
      return await this.get(
        this.withEcardsWeekly(
          `/dashboard_weekly_training/weekly_training_detail/${id}`,
          true
        )
      )
    }
    return await this.get(`${SNOW_FLAKE_URL}/trainings/get/${id}`)
  }

  async getDealerDetails(id, type) {
    return await this.get(
      `/dealers_training/training_detail/${id}?connectType=${type}`
    )
  }

  async getScreenDetails(id, type) {
    return await this.get(
      `/dealers_training/screened_candidates/${id}?${type}=true`
    )
  }

  async getAttendees(
    pagination,
    opts?: { useEcardsVivint?: boolean }
  ) {
    const queryString = objectToQueryString(pagination)
    if (opts?.useEcardsVivint) {
      return await this.get(
        this.vivintEcardsAbsolute(
          `/api/vivint/connect/dashboard/all-raffle-entries?${queryString}`
        )
      )
    }
    return await this.get(
      `/dashboard_weekly_training/all_raffle_entries?${queryString}`
    )
  }

  async getRsvp(pagination, opts?: { useEcardsVivint?: boolean }) {
    const queryString = objectToQueryString(pagination)
    if (opts?.useEcardsVivint) {
      return await this.get(
        this.vivintEcardsAbsolute(
          `/api/vivint/connect/dashboard/all-rsvp-entries?${queryString}`
        )
      )
    }
    return await this.get(
      `/dashboard_weekly_training/all_rsvp_entries?${queryString}`
    )
  }
  async getWeeklyMeetings(
    { pageNumber, limit, endDate, startDate },
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.get(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/weekly_board_meeting_training_stats?page=${pageNumber}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        opts?.useEcardsVivint
      )
    )
  }

  async updateColumn(data: any) {
    return await this.post(`${SNOW_FLAKE_URL}/board/update_candidates`, data)
  }

  /** Vivint proxy: inline candidate field updates (Volition). */
  async updateVivintCandidate(data: any) {
    return await this.post(
      this.vivintEcardsAbsolute(`/vivint/candidates/update`),
      data
    )
  }

  async updateAttempts(id: string, data: any) {
    return await this.post(`/dealer_candidate/update_screening/${id}`, data)
  }

  async updateAiFirst(id: string, data: any) {
    return await this.post(`/dealer_candidate/updateCandidate/${id}`, data)
  }

  async trainingLeaders(
    data: any,
    opts?: { useEcardsVivint?: boolean }
  ) {
    return await this.post(
      this.withEcardsWeekly(
        `/dashboard_weekly_training/change_weekly_training_leader`,
        opts?.useEcardsVivint
      ),
      data
    )
  }

  async updateCompanyUser(id: string, data: any) {
    return await this.post(`/dealer_user/update_profile/${id}`, data)
  }

  async dealersContacts(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/dealer_contact/all_contacts?${queryString}`)
  }

  async dealersTrainings(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/dealers_training/all_training?${queryString}`)
  }

  async getDealerRSVP(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(`/dealers_training/all_rsvp?${queryString}`)
  }

  async dealersMediaAds(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(
      `/dealers_social_media_add/dash_board_all_ads?${queryString}`
    )
  }

  async screenerStats(pagination: any) {
    const queryString = objectToQueryString(pagination)
    return await this.get(
      `/dealer_candidate/who-screened-candidate?${queryString}`
    )
  }

  async getCollegeCampuses({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/college_campuses?limit=${limit}&skip=${page - 1}`
    )
  }

  async updateCandidate(payload) {
    return await this.post(`${SNOW_FLAKE_URL}/candidates/update`, payload)
  }
  
  async updateEvoCandidate(id: string, payload: any) {
    return await this.post(`/evo/candidate/update_screening/${id}`, payload)
  }

  async getContacts(pagination) {
    return await this.post(`/get_contacts`, pagination)
  }

  async getContactStats({date, division}) {
    return await this.get(`/contacts/total_count_stats?date=${date}&division=${division}`)
  }

  /** Contacts regional report: GET with query date, groupBy, source, location, contactLeaderId, recruiterId, maxPosition, tenure */
  async getContactsRegionalReporting(params: Record<string, string | undefined>) {
    const q: Record<string, string> = {}
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    const queryString = new URLSearchParams(q).toString()
    return await this.get(
      `/contact_dash_board/regional-reporting${queryString ? `?${queryString}` : ""}`
    )
  }

  /** Contacts: filter dropdown options (sources, locations, maxPositions, tenures). GET */
  async getContactsFilterOptions() {
    return await this.get("/contact_dash_board/filter_options")
  }

  async getFilters(tag) {
    return await this.post(`/contact_dash_board/users`, {
      tag,
    })
  }

  async getContactUsers(tag) {
    return await this.post(`${SNOW_FLAKE_URL}/contact/users`, {
      tag,
    })
  }

  async downloadContacts(pagination) {
    return await this.post(`/get_contacts_download`, pagination)
  }

  async downloadBlitz() {
    return await this.get(`${SNOW_FLAKE_URL}/board/export_blitz`)
  }

  async downloadBlitzUsers(pagination: Record<string, unknown>) {
    const queryString = objectToQueryString({
      ...pagination,
      isDownload: "true",
    })
    return await this.get(
      `/blitz_dash_board/registered_blitz_user?${queryString}`
    )
  }

  async getInfoSessions({ limit, pageNumber }) {
    return await this.get(
      `/online_info_session_dash_board/all_info_session?page=${pageNumber}&limit=${limit}`
    )
  }

  async getInfoSessionsReg({ limit, pageNumber, startDate, endDate }) {
    return await this.get(
      `/online_info_session_dash_board/all_registered_reps?page=${pageNumber}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`
    )
  }

  async getInfoSessionsAttended({ limit, pageNumber, endDate, startDate }) {
    return await this.get(
      `/online_info_session_dash_board/all_attended_reps?page=${pageNumber}&limit=${limit}&endDate=${endDate}&startDate=${startDate}`
    )
  }

  async getScholarships({ limit, page }) {
    return await this.get(
      `${SNOW_FLAKE_URL}/board/scholarships?limit=${limit}&skip=${page - 1}`
    )
  }

  async addImages(data: {
    type: string
    is_active: boolean
    image: File
    location: string
    winner: string
  }) {
    const formData = new FormData()
    formData.append("image", data.image)
    formData.append("type", data.type)
    formData.append("is_active", String(data.is_active))
    formData.append("location", data.location)
    formData.append("winner", data.winner)
    return await this.postWithParams(`/give_away_images/add`, formData)
  }

  async deleteImage(id: string) {
    return await this.delete(`/give_away_images/delete/` + id)
  }

  async deleteDealerMediaAd(id: string) {
    return await this.delete(`/dealers_social_media_add/delete_ad/` + id)
  }

  async updateDealerMediaAd(id: string, data: any) {
    return await this.post(`/dealers_social_media_add/update_ad/` + id, data)
  }

  async requestDealerAd(data: any) {
    return await this.post(
      `/dealers_social_media_add/request_availability`,
      data
    )
  }

  async getGiveaway() {
    return await this.get(`/give_away_images/images`)
  }

  async getGiveawayGallery() {
    return await this.get(`/give_away_images/gallery`)
  }

  async getFeedbacks({ pageSize, pageNumber }) {
    return await this.get(
      `/feedbacks/all_feedback?pageSize=${pageSize}&pageNumber=${pageNumber}`
    )
  }

  async changeImageStatus(id: string, payload: any) {
    return await this.post(`/give_away_images/make_active/${id}`, payload)
  }

  async changeStatus(id: string) {
    return await this.get(`${SNOW_FLAKE_URL}/helper/changestatus/` + id)
  }

  async getDashData(filters: any) {
    this.applyAxiosAuthHeader()
    return await this.get(
      `${SNOW_FLAKE_URL}/partner/dashboard?from_date=${filters.from_date}&to_date=${filters.to_date}&partner=EVO - Card&name=Jason Card`
    )
  }

  async getSpecificOffice(filters: any) {
    this.applyAxiosAuthHeader()
    return await this.get(
      `${SNOW_FLAKE_URL}/partner/dashboard_by_office?from_date=${filters.from_date}&to_date=${filters.to_date}&office=${filters.office}`
    )
  }

  async getOfficeList(filters: any) {
    this.applyAxiosAuthHeader()
    return await this.get(
      `${SNOW_FLAKE_URL}/partner/get_list_office?type=${filters.type}&from_date=${filters.from_date}&to_date=${filters.to_date}&office=${filters.office}`
    )
  }

  // partner/get_list_office?type=leads&from_date=Jan 1, 2023&to_date=May 1, 2024&office=GM Scott Windell

  async getLeadsData(filters: any) {
    return await this.get(
      `${SNOW_FLAKE_URL}/partner/get_list?type=${filters.type}&from_date=${filters.from_date}&to_date=${filters.to_date}&partner=EVO - Card&name=Jason Card`
    )
  }
  // partner/get_list?type=leads&from_date=Jan 1, 2023&to_date=May 1, 2024&partner=EVO - Card&name=Jason Card

  async addVideo(data: any) {
    return await this.post(`${SNOW_FLAKE_URL}/videos/addvideo`, data)
  }

  async addUser(data: any) {
    return await this.post(`${SNOW_FLAKE_URL}/dashboard/signup`, data)
  }

  async addCompanyUser(data: any) {
    return await this.post(`${SNOW_FLAKE_URL}/dealership/adduser`, data)
  }

  async updatePermissions(data: any) {
    return await this.post(
      `${SNOW_FLAKE_URL}/dashboard/update_permissions`,
      data
    )
  }

  async changePassword(data: any) {
    return await this.post(`${SNOW_FLAKE_URL}/dashboard/resetpassword`, data)
  }

  async deleteUser(id: any) {
    return await this.delete(`${SNOW_FLAKE_URL}/dashboard/delete_user/` + id)
  }
}

export default UserService
